import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Check, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth, formatApiError } from "../context/AuthContext";
import { PageShell } from "../components/PageShell";

export default function Pricing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [plans, setPlans] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => { api.get("/billing/plans").then(({ data }) => setPlans(data)); }, []);

  const upgrade = async (plan) => {
    if (!user) return navigate("/login?next=/pricing");
    setBusy(plan);
    try {
      const { data } = await api.post("/payments/checkout", { plan, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e));
      setBusy(null);
    }
  };

  if (!plans) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin" /></div>;
  const card = (key, name, price, sub, features, cta) => (
    <div className="glass rounded-3xl p-7 flex flex-col" data-testid={`plan-${key}`} style={key === "annual" ? { borderColor: "rgba(255,215,0,0.5)", boxShadow: "0 0 30px rgba(255,215,0,0.15)" } : {}}>
      <div className="font-mono-label mb-2">{name}</div>
      <div className="text-4xl font-display mb-1">{price}</div>
      <div className="text-xs text-white/45 mb-6">{sub}</div>
      <ul className="space-y-2 text-sm text-white/75 flex-1 mb-6">
        {features.map((f) => <li key={f} className="flex gap-2"><Check size={14} className="text-cyan-300 mt-0.5 shrink-0" />{f}</li>)}
      </ul>
      {cta}
    </div>
  );
  const premiumCta = (key) => user?.is_premium ? (
    <div className="text-center text-sm text-amber-200" data-testid={`plan-${key}-active`}>You're Premium</div>
  ) : (
    <button onClick={() => upgrade(key)} disabled={!!busy} className="btn-pill btn-pill-gold justify-center" data-testid={`plan-${key}-btn`}>
      {busy === key ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Upgrade
    </button>
  );
  return (
    <PageShell back="/" backLabel="Home" label="Memberships" wide title="Choose how deep you go"
      subtitle="The core experience is free forever. Premium unlocks the full multiverse. Prices are clear, and you can cancel anytime.">
      {plans.stripe_mode === "test" && (
        <div className="glass rounded-2xl px-5 py-3 mb-8 text-xs text-amber-200/80" data-testid="test-mode-banner">
          Payments are in TEST MODE. No real money is charged. Use card 4242 4242 4242 4242, any future date, any CVC.
        </div>
      )}
      <div className="grid md:grid-cols-3 gap-5">
        {card("free", "Free", "$0", "Forever", plans.free.features, <div className="text-center text-sm text-white/50">Included</div>)}
        {card("monthly", "Monthly Premium", `$${plans.monthly.price}`, "per month", plans.premium_features, premiumCta("monthly"))}
        {card("annual", "Annual Premium", `$${plans.annual.price}`, `per year · save ${Math.round((1 - plans.annual.price / (plans.monthly.price * 12)) * 100)}%`, plans.premium_features, premiumCta("annual"))}
      </div>
      <p className="text-[11px] text-white/35 mt-8">All alternate lives are imaginative simulations, not predictions. Fair-use limits apply to AI features to keep the service sustainable.</p>
    </PageShell>
  );
}

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [state, setState] = useState("checking");

  useEffect(() => {
    let tries = 0;
    let timer;
    const poll = async () => {
      tries += 1;
      try {
        const { data } = await api.get(`/payments/status/${params.get("session_id")}`);
        if (data.payment_status === "paid") { setState("paid"); refresh(); return; }
        if (data.status === "failed") { setState("failed"); return; }
      } catch { /* retry */ }
      if (tries < 10) timer = setTimeout(poll, 2000); else setState("pending");
    };
    poll();
    return () => clearTimeout(timer);
  }, [params, refresh]);

  const msg = { checking: "Confirming your payment…", paid: "Welcome to Premium. Every door is open.", failed: "The payment didn't go through.", pending: "Still processing. Refresh in a minute." }[state];
  return (
    <div className="min-h-screen flex flex-col items-center justify-center text-center px-6" data-testid="payment-status">
      {state === "checking" && <Loader2 className="animate-spin mb-4" />}
      <h1 className="text-3xl font-display font-light mb-6" data-testid="payment-status-text">{msg}</h1>
      <button onClick={() => navigate("/")} className="btn-pill btn-pill-primary">Return to the multiverse</button>
    </div>
  );
}
